import moment from "moment";

import { Unit } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Unit";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateTime as DateTimeHelper } from "@/Common/Helpers/DateTime";
import { PrescriptionMedicationStatus } from "@/Common/Models/Patient/PatientEnums";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { EnumPatientSummaryMedicationDisplayStatus } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationDisplayStatus";
import { DosageDisplayOptions } from "@/Patient/Models/Medication/DosageDisplayOptions";
import { MedicationDosageDisplayMode } from "@/Patient/Models/Medication/MedicationModel";

/**
 * Helper class for medication directives.
 * The functions were taken from src\Patient\Models\Medication\MedicationModel.ts and transformed
 * into static methods for easier reuse.
 */
export class MedicationDirectivesHelper {
    public static getEffectiveEndDate(params: { CeaseDate?: Date | string; RefillEndDate?: Date | string }): Date | null {
        if (params.CeaseDate != null) {
            return new Date(params.CeaseDate);
        }
        else if (params.RefillEndDate != null) {
            return new Date(params.RefillEndDate);
        }
        else {
            return null;
        }
    }

    public static getName(model: { Name: string, Form?: string, DrugStrength?: string }, ShowForm: boolean = true): string {
        let form = "";
        let name = model.Name;

        if (ShowForm === true)
            form = (model.Form != null ? " (" + model.Form + ")" : "");

        const drugStrength = model.DrugStrength != null ? " " + model.DrugStrength : "";

        return `${name}${drugStrength}${form}`;
    }

    /**
    * Returns the renewal string for a medication.
    * @param model The medication model object (with primitive fields).
    * @param options Dosage display options.
    */
    public static getRenewalString(
        model: {
            NumberOfRefill?: number;
            SummaryDisplayStatus?: EnumPatientSummaryMedicationDisplayStatus;
            RefillEndDate?: Date;
            TotalDurationManualDays?: number;
            IsCeased: boolean,
        },
        options: Partial<DosageDisplayOptions> = new DosageDisplayOptions()
    ): string {
        if (!model?.NumberOfRefill || model.NumberOfRefill <= 0)
            return "";

        if (model.SummaryDisplayStatus === EnumPatientSummaryMedicationDisplayStatus.ShowExpired)
            return `R0/${model.NumberOfRefill}`;

        if (model.IsCeased)
            return `R${model.NumberOfRefill}`;

        if (model.SummaryDisplayStatus === EnumPatientSummaryMedicationDisplayStatus.ShowActive) {
            if (model.RefillEndDate == null)
                return `R${model.NumberOfRefill}`;

            const nbDaysPerRefill = model.NumberOfRefill > 0
                ? model.TotalDurationManualDays / model.NumberOfRefill
                : 0;

            let nbDaysTillRefillEnd = model.NumberOfRefill > 0
                ? moment(model.RefillEndDate).diff(DateTimeHelper.nowMoment(), "day")
                : 0;

            if (nbDaysTillRefillEnd < 0)
                nbDaysTillRefillEnd = 0;

            const nbRefillsLeft = (model.SummaryDisplayStatus >= 1 && model.NumberOfRefill > 0 && nbDaysPerRefill > 0)
                ? Math.ceil(nbDaysTillRefillEnd / nbDaysPerRefill)
                : 0;

            if (options.includeRemainingRenewalCalculation)
                return `R${nbRefillsLeft}/${model.NumberOfRefill}`;
            return `R${model.NumberOfRefill}`;
        }

        return `R${model.NumberOfRefill}`;
    }

    public static getDosagesDisplay(model: {
        Dosages: {
            DosageUnitValue?: string;
            DosageString?: string;
            DosageFrequency?: string;
            IfNeeded?: boolean;
            Duration?: number;
            DisplayMode?: MedicationDosageDisplayMode;
            DosageServedQuantity?: string;
            DosageServedDays?: string;
            DosageServedFrequency?: string;
            IsFakeDosage?: boolean;
        }[],
        SummaryDisplayStatus?: EnumPatientSummaryMedicationDisplayStatus,
        IsNarcotic: boolean,
        IsCeased: boolean,
        RefillQuantity?: string,
        TotalDurationManual?: number,
        NumberOfRefill?: number;
        RefillEndDate?: Date;
        TotalDurationManualDays?: number;
    }, options: Partial<DosageDisplayOptions> = {}): string[] {
        options = new DosageDisplayOptions(options);
        const dosageDisplayArray: string[] = [];
        const lastDosageIndex = model.Dosages.length - 1;
        model.Dosages.forEach((dosage, idx) => {
        // Use primitive access for properties
            const dosageUnit: string = dosage.DosageUnitValue ?? "";

            let posologie = "";
            if (!dosage.IsFakeDosage) {
                const addPRN = dosage.IfNeeded ? " PRN" : "";
                const dosageStringClean = dosage.DosageString ?? "";
                posologie = `${dosageStringClean} ${dosageUnit} ${this.getFrequencyValueReduced(dosage) ?? ""}${addPRN}`;
            }

            if (options.includeDaysOrQty || model.IsNarcotic) {
                const dosageServedQuantityClean = dosage.DosageServedQuantity
                    ? `#${dosage.DosageServedQuantity} ${!isNaN(dosage.DosageServedQuantity as any) ? dosageUnit : ""}`
                    : "";

                const day = model.TotalDurationManual == null || DateHelper.TicksToDays(dosage.Duration) == 0
                    ? ""
                    : DateHelper.TicksToDays(dosage.Duration).toString() + cacheStore.GetString("DaysLabel").substr(0, 1).toLowerCase();

                const dayOrQty = dosage.DisplayMode == MedicationDosageDisplayMode.Quantity || model.IsNarcotic
                    ? dosageServedQuantityClean
                    : day;

                if (dayOrQty)
                    posologie += ` ${dayOrQty}`;
            }

            if (
                idx === lastDosageIndex
                && options.includeRefillQuantity
                && dosage.DisplayMode !== MedicationDosageDisplayMode.Quantity
            ) {
                if (model.RefillQuantity) {
                    posologie += ` (#${model.RefillQuantity})`;
                }
                else if (dosage.DosageServedQuantity) {
                    posologie += ` (#${dosage.DosageServedQuantity} ${!isNaN(dosage.DosageServedQuantity as any) ? dosageUnit : ""})`;
                }
            }

            if (options.includeRenewalsOnLastDosage && idx == lastDosageIndex) {
                posologie += ` ${this.getRenewalString(model, options)}`;
            }

            dosageDisplayArray.push(posologie.trim());

            // served every...
            if (model.IsNarcotic && options.includeNarcoticFreq) {
                let servedFreq = PatientMedicalItemsHelper.GetMedicationNarcoticFrequency(dosage);
                const servedQty = PatientMedicalItemsHelper.GetMedicationNarcoticQuantity(dosage, dosageUnit);

                if (servedFreq != "")
                    servedFreq += ", ";

                dosageDisplayArray.push(`${servedFreq}${servedQty}`.trim());
            }
        });

        return dosageDisplayArray;
    }

    public static getDosageUnitValue(params: { DosageUnit?: number; DosageTypeLookupId?: string }): string {
        let returnValue = "";

        // Find the unit in the cache
        const units = cacheStore.UnitsCache
            .filter(unit => unit.Id == params.DosageUnit)
            .map(unit => {
                if (unit.Id == 2) {
                    unit.CodeEn = "A.D.";
                    unit.CodeFr = "S.P.";
                    return unit;
                }
                else {
                    return unit;
                }
            });

        if (units != null && units.length > 0)
            returnValue += Unit.getLocalizedCode(units[0]);

        // Find the dosage type lookup
        const dosageTypeLK = params.DosageTypeLookupId != null
            ? cacheStore.GetLookupById(params.DosageTypeLookupId)
            : cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType");

        if (dosageTypeLK != null) {
            if (dosageTypeLK.LookupCode == "DU.PerDay")
                returnValue += "/" + cacheStore.GetString("DaysLabel").substr(0, 1).toLowerCase();
            else if (dosageTypeLK.LookupCode == "DU.PerMinute")
                returnValue += "/min";
            else if (dosageTypeLK.LookupCode == "DU.PerHour")
                returnValue += "/h";
        }

        return returnValue;
    }

    public static getMedicationQuantities(model: {
        dosages: {
            DosageServedQuantity?: string;
            DisplayMode?: MedicationDosageDisplayMode;
            DosageUnit?: number;
            DosageTypeLookupId?: string;
        }[],
        refillQuantity: string,
        initialRefillQuantity: string
    }): string[] {
        const validateDosageQuantity = (dosage: { DosageServedQuantity?: string }): boolean =>
            !!dosage.DosageServedQuantity && !isNaN(Number(dosage.DosageServedQuantity));

        if (model.dosages.length > 1) {
            const lastDose = model.dosages[model.dosages.length - 1];
            const quantityUnit = validateDosageQuantity(lastDose) ? this.getDosageUnitValue(lastDose) : "";

            const refillQty = lastDose.DisplayMode === MedicationDosageDisplayMode.Quantity
                ? `${lastDose.DosageServedQuantity} ${quantityUnit}`
                : model.refillQuantity;

            if (!IsNullOrWhiteSpace(model.initialRefillQuantity)) {
                const initialRefillQuantityInString = `${cacheStore.GetString("FirstDispensationShortLabel")}: ${model.initialRefillQuantity}`;
                if (refillQty) {
                    const refillQuantityInString = `${cacheStore.GetString("RefillQuantityAcronymLabel")}: ${refillQty}`;
                    return [initialRefillQuantityInString, refillQuantityInString];
                }
                else {
                    return [initialRefillQuantityInString];
                }
            }
            return [refillQty];
        }
        else if (model.dosages.length === 1) {
            const dosage = model.dosages[0];
            if (!IsNullOrWhiteSpace(dosage.DosageServedQuantity))
                return [`${dosage.DosageServedQuantity} ${validateDosageQuantity(dosage) ? this.getDosageUnitValue(dosage) : ""}`];
        }
        return [];
    }

    public static getDurationInDaysString(totalDurationManualDays: number): string {
        return totalDurationManualDays
            ? `${totalDurationManualDays} ${cacheStore.GetString("DaysLabel").toLowerCase()}`
            : "";
    }

    public static getDosageString(
        model: {
            Dosages: {
                DosageUnitValue?: string;
                DosageString?: string;
                DosageFrequency?: string;
                IfNeeded?: boolean;
                Duration?: number;
                DisplayMode?: MedicationDosageDisplayMode;
                DosageServedQuantity?: string;
                DosageServedDays?: string;
                DosageServedFrequency?: string;
                IsFakeDosage?: boolean;
            }[],
            RefillEndDate?: Date;
            NumberOfRefill?: number;
            SummaryDisplayStatus?: EnumPatientSummaryMedicationDisplayStatus;
            IsNarcotic: boolean;
            RefillQuantity?: string;
            PrescriptionStatus?: PrescriptionMedicationStatus;
            TotalDurationManual?: number;
            IsCeased: boolean;
            TotalDurationManualDays?: number;
        },
        options?: Partial<DosageDisplayOptions>
    ): string {
        if (!model || model.Dosages?.length === 0)
            return "";

        if (model.Dosages == null)
            return "...";

        return this.getDosagesDisplay(model, { ...options, includeRenewalsOnLastDosage: false }).pop();
    }

    public IsCeased({
        PrescriptionStatus
    }: {
        PrescriptionStatus: PrescriptionMedicationStatus
    }): boolean {
        return [
            PrescriptionMedicationStatus.Interrupted,
            PrescriptionMedicationStatus.Augmented,
            PrescriptionMedicationStatus.Reduced,
            PrescriptionMedicationStatus.Suspended,
            PrescriptionMedicationStatus.Cancelled
        ].contains(PrescriptionStatus);
    }

    public static getFrequencyValueReduced(dosage?: {
        DosageTypeLookupId?: string;
        DosageFrequency?: string;
    }): string {
        if (dosage) {
            let returnValue = "";
            const dosageTypeLK = dosage.DosageTypeLookupId
                ? cacheStore.GetLookupById(dosage.DosageTypeLookupId)
                : cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType");
            if (dosageTypeLK != null) {
                if (dosageTypeLK.LookupCode == "DU.PerDay")
                    returnValue += cacheStore.GetString("DividedLabel") + " ";
                else if (dosageTypeLK.LookupCode == "DU.PerMinute")
                    returnValue += cacheStore.GetString("MedicationTakenLabel") + " ";
                else if (dosageTypeLK.LookupCode == "DU.PerHour")
                    returnValue += cacheStore.GetString("MedicationTakenLabel") + " ";
            }
            let codeToDisplay = dosage.DosageFrequency;
            returnValue += codeToDisplay
                ? codeToDisplay
                    .replace("weeks", "wk.")
                    .replace("week", "wk.")
                    .replace("months", "mo.")
                    .replace("month", "mo.")
                : "";
            return returnValue;
        }
        return "";
    }
}
